import { Box, Button, Stack, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { GlassPanel } from '../../../shared/components/GlassPanel';
import { IconTile } from '../../../shared/components/IconTile';
import { Pill } from '../../../shared/components/Pill';
import type { Document, DocumentType, Job } from '../../../core/types/data-models';
import { JobChip } from './JobChip';
import { getJobDocTypeName } from './jobDisplay';

// A PROCESSING job running this long has likely been orphaned (e.g. a backend
// restart mid-run) rather than genuinely still working, so a manual Cancel
// action is surfaced past this point. The backend's own stale-job reaper
// (job.worker.ts) auto-recovers sooner (20 min) since it watches for no
// progress updates rather than total runtime — this is a separate, user-facing
// backstop for jobs the reaper hasn't (yet) caught.
const JOB_STALL_MS = 30 * 60 * 1000;

/** "In Progress" block — one JobChip per running job (plus the unresolved questions job, if any), with a Cancel for stalled ones. */
export function ActiveJobsBlock({
  jobs,
  docById,
  docTypeMap,
  onOpenQuestions,
  onCancelJob,
  cancellingJobId,
}: {
  jobs: Job[];
  docById: Map<number, Document>;
  docTypeMap: Map<string, DocumentType>;
  onOpenQuestions: (job: Job) => void;
  onCancelJob: (job: Job) => void;
  cancellingJobId: number | null;
}) {
  return (
    <GlassPanel sx={{ p: 2.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <IconTile icon={<AutoAwesomeIcon fontSize="inherit" />} color="primary" size="sm" />
        <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1 }}>
          In Progress
        </Typography>
        <Pill color="primary">{jobs.length}</Pill>
      </Stack>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {jobs.map((job) => {
          const isStalled = job.status === 'PROCESSING' && Date.now() - new Date(job.createdAt).getTime() >= JOB_STALL_MS;
          return (
            <Stack key={job.id} direction="row" spacing={0.5} alignItems="center">
              <JobChip
                job={job}
                docTypeName={getJobDocTypeName(job, docById, docTypeMap)}
                onClick={job.status === 'COMPLETED' ? () => onOpenQuestions(job) : undefined}
              />
              {isStalled && (
                <Button
                  size="small"
                  color="error"
                  disabled={cancellingJobId === job.id}
                  onClick={() => onCancelJob(job)}
                >
                  Cancel
                </Button>
              )}
            </Stack>
          );
        })}
      </Box>
    </GlassPanel>
  );
}
